import {
  Badge,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui";
import { useIsMobile } from "@/hooks/use-mobile";
import { actionsKeys, cn, incidentStatus, permissionActions, toApiSort } from "@/lib/utils";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { useGetIncidentList } from "@/services";
import { Check, Edit, MoreVertical, X } from "lucide-react";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { priorityOptions } from "./AddEdit/consts";

const useIncidentMgmt = () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [globalFilter, setGlobalFilter] = useState<any>();
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [sorting, setSorting] = useState([{ id: "createdAt", desc: true }]);

  const communityDetail = useSelector(
    (state: RootState) => state.currentData.communityData
  );

  const accessPermission = useSelector(
    (state: RootState) => state.currentData.accessPermission
  );

  const hasAddPermission = useMemo(() => {
    return accessPermission?.[actionsKeys?.INCIDENT_MANAGEMENT?.toLowerCase()]?.[permissionActions.CREATE]
  }, []);

  const hasUpdatePermission = useMemo(() => {
    return accessPermission?.[actionsKeys?.INCIDENT_MANAGEMENT?.toLowerCase()]?.[permissionActions.UPDATE]
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const {
    data: incidentList,
    isLoading: isIncidentLoading,
    refetch: refetchIncident,
    isRefetching: isIncidentRefetching,
  } = useGetIncidentList({
    communityId: communityDetail?.communityId,
    data: {
      filters: [
        {
          field: "communityId",
          value: communityDetail?.communityId,
          operator: "EQUAL",
        },
      ],
      pageNumber,
      pageSize,
      sort: toApiSort(sorting, { field: "createdAt", direction: "DESC" }),
    },
    queryParams: {
      enabled: !!communityDetail?.communityId,
    },
  });

  useEffect(() => {
    refetchIncident();
  }, [pageNumber, pageSize, sorting]);

  useEffect(() => {
    if (globalFilter?.length >= 2 || globalFilter?.length == 0) {
      refetchIncident();
    }
  }, [globalFilter]);

  // Define Table Columns
  const columns = [
    {
      accessorKey: "incidentId",
      header: "ID",
      accessorFn: (row: any) => String(row.incidentId || ""),
    },
    {
      accessorKey: "incidentType",
      header: "Type",
      accessorFn: (row: any) => String(row.incidentType || ""),
    },
    {
      accessorKey: "incidentCategoryName",
      header: "Category",
      accessorFn: (row: any) => String(row.incidentCategoryName || ""),
    },
    {
      accessorKey: "commonAreaFlag",
      header: "Common Area",
      cell: (row: any) =>
        row.row.original.commonAreaFlag ? (
          <Check className="h-[20px] w-[20px] stroke-green-600" />
        ) : (
          <X className="h-[20px] w-[20px] stroke-red-600" />
        ),
    },
    // {
    //   accessorKey: "assignTo",
    //   header: "Assign To",
    //   accessorFn: (row: any) => String(row.commonArea),
    // },
    {
      accessorKey: "assignedToUserName",
      header: "Responsible",
      accessorFn: (row: any) => String(row.assignedToUserName || ""),
    },
    {
      accessorKey: "priority",
      header: "Priority",
      accessorFn: (row: any) =>
        row.priority
          ? priorityOptions.find(
              (data: any) =>
                data.value?.toLowerCase() == row.priority?.toLowerCase()
            )?.label
          : "",
    },
    {
      accessorKey: "createdAt",
      header: "Raised On",
      accessorFn: (row: any) =>
        row.createdAt ? moment(row.createdAt).format("MM/DD/YYYY") : "",
    },
    {
      accessorKey: "reportedByUserName",
      header: "Raised By",
      accessorFn: (row: any) => String(row.reportedByUserName || ""),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: (row: any) => {
        return (
          <Badge
            variant="outline"
            className={cn(
              (incidentStatus as any)[row.row.original.status?.toLowerCase()]
                ?.color,
              "rounded-full px-4 py-2"
            )}
          >
            {
              (incidentStatus as any)[row.row.original.status?.toLowerCase()]
                ?.label
            }
          </Badge>
        );
      },
    },
    ...(hasUpdatePermission ? [{
      id: "actions",
      header: "Actions",
      enableSorting: false,
      cell: (row: any) => (
        <DropdownMenu>
          <DropdownMenuTrigger>
            <MoreVertical className="w-5 h-5 cursor-pointer" />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="bg-white border-0 shadow-md"
          >
            <DropdownMenuItem
              className="flex items-center gap-2"
              disabled={row.row.original.status?.toLowerCase() == "resolved" ||  row?.row?.original?.status?.toLowerCase() === "charged_closed"}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate(`${row.row.original.incidentId}/edit`);
              }}
            >
              <Edit className="w-4 h-4" />
              Edit
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    }] : [])
  ];

  const onAddIncident = () => {
    navigate(routes.add);
  };

  const onRowClick = (row: any) => {
    navigate(`${row.original.incidentId}/view`);
  };

  return {
    communityDetail,
    columns,
    isMobile,
    globalFilter,
    setGlobalFilter,
    onAddIncident,
    onRowClick,
    setPageNumber,
    setPageSize,
    pageNumber,
    pageSize,
    sorting,
    setSorting,
    incidentList,
    isIncidentLoading,
    isIncidentRefetching,
    hasAddPermission
  };
};

export default useIncidentMgmt;
